import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
  Update,
} from '@reduxjs/toolkit'
import { v4 as uuid } from 'uuid'
import {
  EditSalesFunnelState,
  EditSalesFunnelStateUpdate,
  IDisqualifyReasonEditModel,
  ISalesFunnelCreateModel,
  ISalesFunnelEditDTO,
  ISalesFunnelSectionEditModel,
  ISalesFunnelStageEditModel,
  ISectionCreateModel,
} from './typings'
import { StoreData } from '../reducers/typings'
import { getSalesFunnelForEdit } from './api'
import { validateFunnelName } from './functions'

const sectionAdapter = createEntityAdapter<ISalesFunnelSectionEditModel>()
const stageAdapter = createEntityAdapter<ISalesFunnelStageEditModel>({
  sortComparer: (a, b) => a.order - b.order,
})
const reasonAdapter = createEntityAdapter<IDisqualifyReasonEditModel>()

export const initialState: EditSalesFunnelState = {
  funnel: {
    id: uuid(),
    name: '',
    isValid: false,
    allowOtherReasons: false,
    manualDisqualifyReasonCount: 0,
  },
  sections: sectionAdapter.getInitialState(),
  stages: stageAdapter.getInitialState(),
  reasons: reasonAdapter.getInitialState(),
}

const editSalesFunnel = createSlice({
  name: 'editSalesFunnel',
  initialState,
  reducers: {
    resetEditFunnel: (state) => {
      state.funnel = { ...initialState.funnel }

      const leadSection: ISalesFunnelSectionEditModel = {
        id: uuid(),
        name: 'Lead Status',
        order: 0,
      }
      const oppSection: ISalesFunnelSectionEditModel = {
        id: uuid(),
        name: 'Opportunity Stage',
        order: 1,
      }
      sectionAdapter.setAll(state.sections, [leadSection, oppSection])

      const leadStatuses: ISalesFunnelStageEditModel[] = [
        {
          id: uuid(),
          sectionId: leadSection.id,
          name: 'Qualify',
          isValid: true,
          locked: true,
          order: 0,
          recordCount: 0,
        },
        {
          id: uuid(),
          sectionId: leadSection.id,
          name: 'Disqualified',
          isValid: true,
          locked: true,
          order: 1,
          recordCount: 0,
        },
      ]
      const oppStages: ISalesFunnelStageEditModel[] = [
        {
          id: uuid(),
          sectionId: oppSection.id,
          name: 'Follow Up',
          isValid: true,
          locked: false,
          persistent: true,
          order: 0,
          recordCount: 0,
        },
        {
          id: uuid(),
          sectionId: oppSection.id,
          name: 'Closed/Won',
          isValid: true,
          locked: true,
          order: 1,
          recordCount: 0,
        },
        {
          id: uuid(),
          sectionId: oppSection.id,
          name: 'Closed/Lost',
          isValid: true,
          locked: true,
          order: 2,
          recordCount: 0,
        },
      ]
      stageAdapter.setAll(state.stages, [...leadStatuses, ...oppStages])

      const disqualifyReason: IDisqualifyReasonEditModel = {
        id: uuid(),
        sectionId: leadSection.id,
        reason: 'Not Interested',
        isValid: true,
        recordCount: 0,
      }
      const closeLostReason: IDisqualifyReasonEditModel = {
        id: uuid(),
        sectionId: oppSection.id,
        reason: 'Lost to Competitor',
        isValid: true,
        recordCount: 0,
      }
      reasonAdapter.setAll(state.reasons, [disqualifyReason, closeLostReason])
    },
    setEditFunnelName: (state, { payload }: PayloadAction<string>) => {
      state.funnel.name = payload
      state.funnel.isValid = validateFunnelName(payload)
    },
    setEditFunnelOtherReasons: (state, { payload }: PayloadAction<boolean>) => {
      state.funnel.allowOtherReasons = payload
    },
    addEditFunnelStage: (
      state,
      { payload: sectionId }: PayloadAction<string>,
    ) => {
      const section = sectionAdapter
        .getSelectors()
        .selectById(state.sections, sectionId)

      if (section) {
        const stages = aggregateStagesBySectionId(
          stageAdapter.getSelectors().selectAll(state.stages),
          sectionId,
        )

        const newStage: ISalesFunnelStageEditModel = {
          id: uuid(),
          sectionId: sectionId,
          name: '',
          isValid: false,
          order: -1,
          recordCount: 0,
        }

        switch (section.name) {
          case 'Lead Status':
            // new should always be first, disqualified should always be last
            const lastStage = stages.pop()
            if (!lastStage) return
            const copyLastStage = { ...lastStage }
            newStage.order = lastStage.order
            copyLastStage.order += 1
            stageAdapter.upsertMany(state.stages, [newStage, copyLastStage])
            break
          case 'Opportunity Stage':
            // closed/won and closed/lost should always be the last 2
            const len = stages.length
            const lost = stages.pop()
            const won = stages.pop()
            if (!won || !lost) return
            const copyWon = { ...won }
            const copyLost = { ...lost }
            newStage.order = len - 2
            copyWon.order++
            copyLost.order++
            stageAdapter.upsertMany(state.stages, [newStage, copyWon, copyLost])
            break
        }
      }
    },
    setEditFunnelStageName: (
      state,
      { payload }: PayloadAction<{ id: string; name: string }>,
    ) => {
      const { id, name } = payload
      const selectors = stageAdapter.getSelectors()
      const stage = selectors.selectById(state.stages, id)
      if (!stage) return

      stageAdapter.updateOne(state.stages, {
        id,
        changes: { name },
      })
      validateFunnelStages(state, stage.sectionId)
    },
    setEditFunnelStagesOrder: (
      state,
      { payload }: PayloadAction<{ id: string; order: number }[]>,
    ) => {
      const update: Update<ISalesFunnelStageEditModel>[] = payload.map((s) => ({
        id: s.id,
        changes: { order: s.order },
      }))
      stageAdapter.updateMany(state.stages, update)
    },
    deleteEditFunnelStage: (
      state,
      { payload: stageId }: PayloadAction<string>,
    ) => {
      const selectors = stageAdapter.getSelectors()
      const stage = selectors.selectById(state.stages, stageId)
      if (!stage) return
      stageAdapter.removeOne(state.stages, stageId)
      validateFunnelStages(state, stage.sectionId)
    },
    addEditFunnelReason: (
      state,
      { payload: sectionId }: PayloadAction<string>,
    ) => {
      reasonAdapter.addOne(state.reasons, {
        id: uuid(),
        sectionId,
        reason: '',
        isValid: false,
        recordCount: 0,
      })
    },
    setEditFunnelReason: (
      state,
      { payload }: PayloadAction<{ id: string; reason: string }>,
    ) => {
      const { id, reason } = payload
      const r = reasonAdapter.getSelectors().selectById(state.reasons, id)
      if (!r) return
      reasonAdapter.updateOne(state.reasons, { id, changes: { reason } })
      validateDisqualifyReasons(state, r.sectionId)
    },
    deleteEditFunnelReason: (
      state,
      { payload: reasonId }: PayloadAction<string>,
    ) => {
      const r = reasonAdapter.getSelectors().selectById(state.reasons, reasonId)
      if (!r) return
      reasonAdapter.removeOne(state.reasons, reasonId)
      validateDisqualifyReasons(state, r.sectionId)
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getSalesFunnelForEdit.fulfilled, (state, { payload }) => {
      const { funnel, sections, stages, reasons } = toStoreModel(payload)
      state.funnel = funnel
      state.funnel.isValid = validateFunnelName(funnel.name)
      sectionAdapter.setAll(state.sections, sections)
      stageAdapter.setAll(state.stages, stages)
      reasonAdapter.setAll(state.reasons, reasons)
    })
  },
})

export default editSalesFunnel.reducer

export const {
  resetEditFunnel,
  setEditFunnelName,
  setEditFunnelOtherReasons,
  addEditFunnelStage,
  setEditFunnelStageName,
  setEditFunnelStagesOrder,
  deleteEditFunnelStage,
  addEditFunnelReason,
  setEditFunnelReason,
  deleteEditFunnelReason,
} = editSalesFunnel.actions

//#region selectors
export const { selectAll: selectAllEditFunnelSections } =
  sectionAdapter.getSelectors<StoreData>(
    (state) => state.editSalesFunnel.sections,
  )

//#region stages

export const {
  selectAll: selectAllEditFunnelStages,
  selectById: selectEditFunnelStageById,
} = stageAdapter.getSelectors<StoreData>(
  (state) => state.editSalesFunnel.stages,
)

export const aggregateStagesBySectionId = (
  stages: ISalesFunnelStageEditModel[],
  sectionId: string,
) => stages.filter((x) => x.sectionId == sectionId)

//#endregion stages

//#region reasons

export const {
  selectAll: selectAllEditFunnelReasons,
  selectById: selectEditFunnelReasonById,
} = reasonAdapter.getSelectors<StoreData>(
  (state) => state.editSalesFunnel.reasons,
)

export const aggregateReasonsBySectionId = (
  reasons: IDisqualifyReasonEditModel[],
  sectionId: string,
) => reasons.filter((x) => x.sectionId == sectionId)

//#endregion reasons

//#region funnel

export const selectEditFunnel = (state: StoreData) =>
  state.editSalesFunnel.funnel

//#endregion funnel

//#region validation

const allStagesValid = (state: StoreData) => {
  const stages = selectAllEditFunnelStages(state)
  return stages.filter((x) => x.isValid === false).length === 0
}

const allReasonsValid = (state: StoreData) => {
  const sections = selectAllEditFunnelSections(state)
  const reasons = selectAllEditFunnelReasons(state)

  return sections.reduce<boolean>((prev, s) => {
    const filtered = aggregateReasonsBySectionId(reasons, s.id)
    const hasReasons = filtered.length > 0
    const allValid = filtered.filter((x) => x.isValid === false).length === 0
    return prev && hasReasons && allValid
  }, true)
}

const selectCanSaveEditFunnel = (state: StoreData) => {
  const funnel = selectEditFunnel(state)
  const stagesValid = allStagesValid(state)
  const reasonsValid = allReasonsValid(state)
  return funnel.isValid && stagesValid && reasonsValid
}

//#endregion validation

export const selectEditFunnelDetail = (state: StoreData) => ({
  ...selectEditFunnel(state),
  canSave: selectCanSaveEditFunnel(state),
})

export const selectDenormalizedEditFunnel = (state: StoreData) => {
  const funnel = selectEditFunnel(state)
  const sections = selectAllEditFunnelSections(state)
  const stages = selectAllEditFunnelStages(state)
  const reasons = selectAllEditFunnelReasons(state)
  return toCreateModel(
    funnel.id,
    funnel.name,
    funnel.allowOtherReasons,
    sections,
    stages,
    reasons,
  )
}

//#endregion selectors

//#region normalization

export function toCreateModel(
  id: string,
  name: string,
  allowManualDisqualifyReasons: boolean,
  sections: ISalesFunnelSectionEditModel[],
  stages: ISalesFunnelStageEditModel[],
  reasons: IDisqualifyReasonEditModel[],
) {
  const funnel: ISalesFunnelCreateModel = {
    id: isNaN(Number(id)) ? '' : id,
    name,
    sections: [],
    allowManualDisqualifyReasons,
  }

  // sections
  const normSections = {}
  sections.forEach((s) => {
    const { id } = s
    const norm: ISectionCreateModel = { id, stages: [], reasons: [] }
    normSections[id] = norm
    funnel.sections.push({ ...norm, id: isNaN(Number(id)) ? '' : id })
  })

  // user defined stages
  stages
    .filter((x) => !x.locked)
    .forEach((s) => {
      const { id, name, sectionId, persistent } = s
      normSections[sectionId].stages.push({
        id: isNaN(Number(id)) ? '' : id,
        name,
        persistent: persistent || false,
      })
    })

  // reasons
  reasons.forEach((r) => {
    const { id, reason, sectionId } = r
    normSections[sectionId].reasons.push({
      id: isNaN(Number(id)) ? '' : id,
      reason,
    })
  })

  return funnel
}

export function toStoreModel(
  toEdit: ISalesFunnelEditDTO,
): EditSalesFunnelStateUpdate {
  const sections: ISalesFunnelSectionEditModel[] = []
  const stages: ISalesFunnelStageEditModel[] = []
  const reasons: IDisqualifyReasonEditModel[] = []

  toEdit.sections.map((section) => {
    sections.push({
      id: section.id + '',
      name: section.name,
      order: section.order,
    })

    section.stages.map((stage) => {
      stages.push({
        id: stage.id + '',
        name: stage.name,
        order: stage.order,
        locked: stage.systemDefined,
        persistent: stage.persistent,
        sectionId: section.id + '',
        isValid: true,
        recordCount: stage.recordCount,
      })
    })

    section.reasons.map((reason) => {
      reasons.push({
        id: reason.id + '',
        reason: reason.reason,
        sectionId: section.id + '',
        isValid: true,
        recordCount: reason.recordCount,
      })
    })
  })

  return {
    funnel: {
      id: toEdit.id + '',
      name: toEdit.name,
      isValid: true,
      allowOtherReasons: toEdit.allowManualDisqualifyReasons,
      manualDisqualifyReasonCount: toEdit.manualDisqualifyReasonCount,
    },
    sections,
    stages,
    reasons,
  }
}

//#endregion normalization

const validateFunnelStages = (
  state: EditSalesFunnelState,
  sectionId: string,
) => {
  const selectors = stageAdapter.getSelectors()

  const stagesInSection = selectors
    .selectAll(state.stages)
    .filter((x) => x.sectionId == sectionId)

  const map = new Map<string, string[]>()
  stagesInSection.map((stage) => {
    const id = stage.id
    const name = stage.name.trim().toLowerCase()
    const ids = map.get(name) ?? []
    map.set(name, [id, ...ids])
  })

  const updates: Update<ISalesFunnelStageEditModel>[] = []
  map.forEach((ids, name) => {
    // no name: invalid
    if (name == '') {
      ids.map((id) =>
        updates.push({ id, changes: { isValid: false, error: 'Required.' } }),
      )
    }
    // has name, no overlap: valid
    else if (ids.length == 1) {
      ids.map((id) =>
        updates.push({ id, changes: { isValid: true, error: undefined } }),
      )
    }
    // has name, with overlap: invalid
    else {
      ids.map((id) =>
        updates.push({
          id,
          changes: { isValid: false, error: 'Names must be unique.' },
        }),
      )
    }
  })

  stageAdapter.updateMany(state.stages, updates)
}

const validateDisqualifyReasons = (
  state: EditSalesFunnelState,
  sectionId: string,
) => {
  const selectors = reasonAdapter.getSelectors()

  const reasonsInSection = selectors
    .selectAll(state.reasons)
    .filter((x) => x.sectionId == sectionId)

  const map = new Map<string, string[]>()
  reasonsInSection.map((r) => {
    const id = r.id
    const reason = r.reason.trim().toLowerCase()
    const ids = map.get(reason) ?? []
    map.set(reason, [id, ...ids])
  })

  const updates: Update<IDisqualifyReasonEditModel>[] = []
  map.forEach((ids, name) => {
    if (name == '') {
      ids.map((id) =>
        updates.push({ id, changes: { isValid: false, error: 'Required.' } }),
      )
    } else if (name.length > 30) {
      ids.map((id) =>
        updates.push({
          id,
          changes: { isValid: false, error: 'Max length 30 characters.' },
        }),
      )
    } else if (ids.length == 1) {
      ids.map((id) =>
        updates.push({
          id,
          changes: { isValid: true, error: undefined },
        }),
      )
    } else {
      ids.map((id) =>
        updates.push({
          id,
          changes: { isValid: false, error: 'Reasons must be unique.' },
        }),
      )
    }
  })

  reasonAdapter.updateMany(state.reasons, updates)
}
